import React, { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import AnchorAsButton from "../../components/common/AnchorAsButton/AnchorAsButton";
import ToHome from "../../components/common/ToHome/ToHome";
import TranslationSwapper from "../../components/common/TranslationSwapper/TranslationSwapper";
import { logout } from "../../redux/actions/auth/auth";
import { RESET_PROPERTIES } from "../../redux/actionTypes/actionsTypes";
import { ProfileOverviewState } from "../../redux/reducers/getProfile/getProfileOverviewReducer";
import { SnapshotTypeState } from "../../redux/reducers/getSnapshotTypeDisplay/getSnapshotType";
import { selectProfileGetOverview, selectSnapshotTypeDisplay } from "../../redux/selectors/exportSelectorPaths";
import { formatRoll } from "../../utils/formatter";
import trackCustomGA4Event from "../../utils/GA4EventHelpers/trackCustomGA4Event";
import './Header.css';

interface HeaderProps {
  showAutoSave: boolean,
  showRollNumber?: boolean,
  isInternalUser: boolean,
  setSearchInput?: Dispatch<SetStateAction<string>>
}

function Header(props: HeaderProps): JSX.Element {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
  const snapshotDisplayState: SnapshotTypeState = useSelector(selectSnapshotTypeDisplay)
  const address = profileOverview.profile.profile_address
  const rollNum = sessionStorage.getItem("rollNumber")
  const username = localStorage.getItem("username")

  const logoutUser = () => {
    trackCustomGA4Event("app_exit",
      {
        path: window.location.pathname,
        exit_method: "sign_out_button"
      });
    dispatch(logout());
    localStorage.clear();
    sessionStorage.removeItem("wac")
    dispatch({ type: RESET_PROPERTIES })

    if (process.env.REACT_APP_IS_INTERNAL_USER === "1") {
      window.location.href = `${process.env.REACT_APP_INTERNAL_PORTAL_URL}#/login`;
    } else {
      window.location.href = `${process.env.REACT_APP_AMP_URL}/#/auth/home`;
    }
  }

  const goToContactUsPage = () => {
    window.open(t("contact-link"), "_blank", "noreferrer");
    trackCustomGA4Event("external_link",
      {
        link: "contact_us"
      });
  }

  const goToHelpCentrePage = () => {
    window.open(t("help-link"), "_blank", "noreferrer");
    trackCustomGA4Event("external_link",
      {
        link: "help_centre"
      });
  }

  return (
    <header>
      <div>
        {props.isInternalUser && <>
          <p className="internal-user-tag">Internal User View{snapshotDisplayState.display}</p>
          {username && <p className="strong user">User: {username}</p>}
        </>}
      </div>

      {window.location.href.includes("#/portal") &&
        <div className="roll-address-container">

          {(props.showRollNumber !== false) && (
            <>
              {rollNum && <p className="strong roll-address-info">{formatRoll(rollNum)}</p>}
              {address && <p className="roll-address-info">{address?.length > 260 ? address?.slice(0, 260) + "..." : address}</p>}
            </>
          )}
        </div>
      }
      <div className="header-links">
        <div className="header-links-first-row">
          <div className="header-text">
            <AnchorAsButton onClick={() => window.open(t("accessibility-link"), "_blank", "noreferrer")}>{t("accessibility")}</AnchorAsButton>
          </div>
          <div className="lang-access-item">
            <a href={t("screen-reader-link")} target="blank" title="Opens a new window">
              <img src="/images/ea_app_icon.png" alt={t("screen-reader-link")} />
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className='external-link-icon-img' />
            </a>
          </div>
          {props.isInternalUser ? <div className="translation-container-internal"></div> : <div className="translation-container header-text"><TranslationSwapper /></div>}
        </div>
        <div className="header-links-second-row">
          <ToHome>
            <div className="header-text">
              <AnchorAsButton>{t("home")}</AnchorAsButton>
            </div>
          </ToHome>
          <div className="nav-divider">|</div>
          <div className="header-text">
            <AnchorAsButton onClick={goToHelpCentrePage}
            >{t("helpcentre")}</AnchorAsButton>
          </div>
          <div className="nav-divider">|</div>
          <div className="header-text">
            <AnchorAsButton onClick={() => logoutUser()}>
              {props.isInternalUser ? t("new-search") : t("signout")}</AnchorAsButton>
          </div>
          <div className="nav-divider">|</div>
          <div className="header-text">
            <AnchorAsButton onClick={goToContactUsPage}
            >{t("contact-us")}</AnchorAsButton>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
